import { useState, useEffect, useRef } from "react";
import { useLingui } from "@lingui/react/macro";
import { Search, XCircle, CheckCircle2, SlidersHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

export interface IngredientFilters {
  /** Null means every concept. Shared ingredients list under all of them. */
  companyId: number | null;
  structure: ("compound" | "raw")[];
  costMin: string;
  costMax: string;
  units: string[];
  allergenMode: "" | "with" | "without";
  allergenSearch: string;
  selectedAllergens: string[];
  supportsScaling: boolean;
  hasLinkedRecipes: boolean;
}

export const EMPTY_FILTERS: IngredientFilters = {
  companyId: null,
  structure: [],
  costMin: "",
  costMax: "",
  units: [],
  allergenMode: "",
  allergenSearch: "",
  selectedAllergens: [],
  supportsScaling: false,
  hasLinkedRecipes: false,
};

export function countActiveFilters(f: IngredientFilters): number {
  let n = 0;
  if (f.companyId != null) n++;
  if (f.structure.length) n++;
  if (f.costMin || f.costMax) n++;
  if (f.units.length) n++;
  if (f.allergenMode) n++;
  if (f.selectedAllergens.length) n++;
  if (f.supportsScaling) n++;
  if (f.hasLinkedRecipes) n++;
  return n;
}

const STANDARD_UNITS = ["kg", "g", "l", "ml", "psc", "oz"];
const MAX_ALLERGENS_DEFAULT = 6;

interface IngredientFilterPanelProps {
  filters: IngredientFilters;
  onApply: (filters: IngredientFilters) => void;
  availableUnits?: string[];
  availableAllergens?: { code: string; name: string }[];
  availableConcepts?: { id: number; name: string }[];
}

export function IngredientFilterPanel({
  filters,
  onApply,
  availableUnits = [],
  availableAllergens = [],
  availableConcepts = [],
}: IngredientFilterPanelProps) {
  const { t } = useLingui();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<IngredientFilters>(filters);
  const [showAllAllergens, setShowAllAllergens] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const wasOpenRef = useRef(open);

  // Reset draft from external filters only when the panel transitions closed -> open.
  useEffect(() => {
    const openedNow = open && !wasOpenRef.current;
    if (openedNow) {
      setDraft(filters);
      setShowAllAllergens(false);
    }
    wasOpenRef.current = open;
  }, [open, filters]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const update = (patch: Partial<IngredientFilters>) =>
    setDraft((prev) => ({ ...prev, ...patch }));

  const toggleStructure = (type: "compound" | "raw") =>
    update({
      structure: draft.structure.includes(type)
        ? draft.structure.filter((s) => s !== type)
        : [...draft.structure, type],
    });

  const toggleUnit = (unit: string) =>
    update({
      units: draft.units.includes(unit)
        ? draft.units.filter((u) => u !== unit)
        : [...draft.units, unit],
    });

  const toggleAllergen = (code: string) =>
    update({
      selectedAllergens: draft.selectedAllergens.includes(code)
        ? draft.selectedAllergens.filter((c) => c !== code)
        : [...draft.selectedAllergens, code],
    });

  const toggleAllergenMode = (mode: "with" | "without") =>
    update({ allergenMode: draft.allergenMode === mode ? "" : mode });

  const allUnits = [...new Set([...STANDARD_UNITS, ...availableUnits])];

  const filteredAllergens = availableAllergens.filter(
    (a) =>
      !draft.allergenSearch ||
      a.name.toLowerCase().includes(draft.allergenSearch.toLowerCase()) ||
      a.code.toLowerCase().includes(draft.allergenSearch.toLowerCase()),
  );
  const shownAllergens = showAllAllergens
    ? filteredAllergens
    : filteredAllergens.slice(0, MAX_ALLERGENS_DEFAULT);

  const activeFilterCount = countActiveFilters(filters);

  return (
    <div ref={containerRef} className="relative">
      {/* Trigger */}
      <Button
        size="sm"
        onClick={() => setOpen((v) => !v)}
        className={`rounded-[8px] ${
          activeFilterCount > 0 || open
            ? ""
            : "border border-(--color-border) bg-(--color-surface-content) text-(--color-text-primary) hover:bg-(--color-surface-muted)"
        }`}
        variant={activeFilterCount > 0 || open ? "default" : "ghost"}
      >
        <SlidersHorizontal className="size-4" />
        {t`Filter`}
        {activeFilterCount > 0 && (
          <span className="inline-flex size-4 items-center justify-center rounded-full bg-(--color-surface-content) text-[10px] font-bold text-(--color-text-primary)">
            {activeFilterCount}
          </span>
        )}
      </Button>

      {/* Dropdown */}
      {open && (
        <div className="absolute right-0 top-full z-50 mt-1.5 flex w-105 flex-col rounded-[10px] border border-(--color-border) bg-(--color-surface-content) shadow-lg">
          <div className="max-h-130 overflow-y-auto">
            {/* Search ingredient */}
            <div className="border-b border-(--color-border) px-4 py-3">
              <div className="flex items-center gap-2 rounded-[8px] border border-(--color-border) bg-(--color-surface-content) px-3 py-2 shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]">
                <Search className="size-4 shrink-0 text-(--color-text-secondary)" />
                <input
                  type="text"
                  placeholder={t`Search ingredient name...`}
                  className="min-w-0 flex-1 bg-transparent text-sm text-(--color-text-primary) outline-none placeholder:text-(--color-text-secondary)"
                />
              </div>
            </div>

            {/* Concept */}
            {availableConcepts.length > 0 && (
              <div className="border-b border-(--color-border) px-4 py-3">
                <p className="mb-2.5 text-sm font-medium text-(--color-text-primary)">{t`Concept`}</p>
                <select
                  aria-label={t`Concept`}
                  value={draft.companyId == null ? "" : String(draft.companyId)}
                  onChange={(e) =>
                    update({ companyId: e.target.value === "" ? null : Number(e.target.value) })
                  }
                  className="h-9 w-full rounded-[8px] border border-(--color-border) bg-(--color-surface-content) px-3 text-sm text-(--color-text-primary) outline-none"
                >
                  <option value="">{t`All concepts`}</option>
                  {availableConcepts.map((concept) => (
                    <option key={concept.id} value={String(concept.id)}>
                      {concept.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Structure */}
            <div className="border-b border-(--color-border) px-4 py-3">
              <p className="mb-2.5 text-sm font-medium text-(--color-text-primary)">{t`Structure`}</p>
              <div className="flex flex-col gap-2">
                <label className="flex cursor-pointer items-center gap-3">
                  <Checkbox
                    checked={draft.structure.includes("compound")}
                    onCheckedChange={() => toggleStructure("compound")}
                  />
                  <span className="text-sm text-(--color-text-primary)">{t`Sub-recipe`}</span>
                </label>
                <label className="flex cursor-pointer items-center gap-3">
                  <Checkbox
                    checked={draft.structure.includes("raw")}
                    onCheckedChange={() => toggleStructure("raw")}
                  />
                  <span className="text-sm text-(--color-text-primary)">{t`Base Ingredient`}</span>
                </label>
              </div>
            </div>

            {/* Cost */}
            <div className="border-b border-(--color-border) px-4 py-3">
              <p className="mb-2.5 text-sm font-medium text-(--color-text-primary)">{t`Cost`}</p>
              <div className="flex gap-3">
                <div className="flex-1">
                  <p className="mb-1.5 text-xs text-(--color-text-secondary)">{t`Min`}</p>
                  <div className="flex items-center gap-1 rounded-[8px] border border-(--color-border) bg-(--color-surface-content) px-3 py-2 shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]">
                    <span className="text-sm text-(--color-text-secondary)">$</span>
                    <input
                      type="number"
                      min={0}
                      step={0.01}
                      value={draft.costMin}
                      onChange={(e) => update({ costMin: e.target.value })}
                      placeholder="0.00"
                      className="min-w-0 flex-1 bg-transparent text-sm text-(--color-text-primary) outline-none placeholder:text-(--color-text-secondary)"
                    />
                  </div>
                </div>
                <div className="flex-1">
                  <p className="mb-1.5 text-xs text-(--color-text-secondary)">{t`Max`}</p>
                  <div className="flex items-center gap-1 rounded-[8px] border border-(--color-border) bg-(--color-surface-content) px-3 py-2 shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]">
                    <span className="text-sm text-(--color-text-secondary)">$</span>
                    <input
                      type="number"
                      min={0}
                      step={0.01}
                      value={draft.costMax}
                      onChange={(e) => update({ costMax: e.target.value })}
                      placeholder="500.00"
                      className="min-w-0 flex-1 bg-transparent text-sm text-(--color-text-primary) outline-none placeholder:text-(--color-text-secondary)"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Unit of Measure */}
            <div className="border-b border-(--color-border) px-4 py-3">
              <p className="mb-2.5 text-sm font-medium text-(--color-text-primary)">{t`Unit of Measure`}</p>
              <div className="flex flex-wrap gap-2">
                {allUnits.map((unit) => {
                  const active = draft.units.includes(unit);
                  return (
                    <label
                      key={unit}
                      className={`flex cursor-pointer items-center gap-1.5 rounded-[6px] border px-3 py-1.5 transition-colors ${
                        active
                          ? "border-(--color-action) bg-(--color-action) text-(--color-action-fg)"
                          : "border-(--color-border) bg-(--color-surface-content) text-(--color-text-primary) hover:bg-(--color-surface-muted)"
                      }`}
                    >
                      <Checkbox
                        checked={active}
                        onCheckedChange={() => toggleUnit(unit)}
                        className={
                          active
                            ? "border-white data-[state=checked]:bg-(--color-surface-content) data-[state=checked]:text-(--color-text-primary)"
                            : ""
                        }
                      />
                      <span className="text-sm">{unit}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Allergens */}
            <div className="border-b border-(--color-border) px-4 py-3">
              <p className="mb-2.5 text-sm font-medium text-(--color-text-primary)">{t`Allergens`}</p>
              <div className="mb-2.5 flex flex-col gap-2">
                <label className="flex cursor-pointer items-center gap-3">
                  <Checkbox
                    checked={draft.allergenMode === "with"}
                    onCheckedChange={() => toggleAllergenMode("with")}
                  />
                  <span className="text-sm text-(--color-text-primary)">{t`Contains allergens`}</span>
                </label>
                <label className="flex cursor-pointer items-center gap-3">
                  <Checkbox
                    checked={draft.allergenMode === "without"}
                    onCheckedChange={() => toggleAllergenMode("without")}
                  />
                  <span className="text-sm text-(--color-text-primary)">{t`No allergens`}</span>
                </label>
              </div>
              {availableAllergens.length > 0 && (
                <>
                  <div className="mb-2.5 flex items-center gap-2 rounded-[8px] border border-(--color-border) bg-(--color-surface-content) px-3 py-2 shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]">
                    <Search className="size-4 shrink-0 text-(--color-text-secondary)" />
                    <input
                      type="text"
                      placeholder={t`Find allergens...`}
                      value={draft.allergenSearch}
                      onChange={(e) => update({ allergenSearch: e.target.value })}
                      className="min-w-0 flex-1 bg-transparent text-sm text-(--color-text-primary) outline-none placeholder:text-(--color-text-secondary)"
                    />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {shownAllergens.map((allergen) => {
                      const active = draft.selectedAllergens.includes(allergen.code);
                      return (
                        <Button
                          key={allergen.code}
                          onClick={() => toggleAllergen(allergen.code)}
                          variant={active ? "default" : "ghost"}
                          className={`h-auto rounded-[6px] px-3 py-1.5 text-sm ${active ? "" : "border border-(--color-border) bg-(--color-surface-content) text-(--color-text-primary) hover:bg-(--color-surface-muted)"}`}
                        >
                          {allergen.name}
                        </Button>
                      );
                    })}
                    {filteredAllergens.length > MAX_ALLERGENS_DEFAULT && (
                      <Button
                        variant="brand-outline"
                        onClick={() => setShowAllAllergens((v) => !v)}
                        className="h-auto rounded-[6px] px-3 py-1.5 text-sm"
                      >
                        {showAllAllergens ? t`Show less` : t`Show more`}
                      </Button>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Other filters */}
            <div className="px-4 py-3">
              <p className="mb-2.5 text-sm font-medium text-(--color-text-primary)">{t`Other filters`}</p>
              <div className="flex flex-col gap-2">
                <label className="flex cursor-pointer items-center gap-3">
                  <Checkbox
                    checked={draft.supportsScaling}
                    onCheckedChange={(checked) => update({ supportsScaling: !!checked })}
                  />
                  <span className="text-sm text-(--color-text-primary)">{t`Supports scaling`}</span>
                </label>
                <label className="flex cursor-pointer items-center gap-3">
                  <Checkbox
                    checked={draft.hasLinkedRecipes}
                    onCheckedChange={(checked) => update({ hasLinkedRecipes: !!checked })}
                  />
                  <span className="text-sm text-(--color-text-primary)">{t`Has linked recipes`}</span>
                </label>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between border-t border-(--color-border) px-4 py-3">
            <Button
              variant="ghost"
              onClick={() => setDraft(EMPTY_FILTERS)}
              className="h-auto gap-2 px-0 py-0 text-sm text-(--color-text-secondary) hover:bg-transparent hover:text-(--color-text-primary)"
            >
              <XCircle className="size-4" />
              {t`Reset all filters`}
            </Button>
            <div className="flex items-center gap-2">
              <Button
                variant="brand-outline"
                size="sm"
                onClick={() => setOpen(false)}
                className="rounded-[8px]"
              >
                {t`Cancel`}
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  onApply(draft);
                  setOpen(false);
                }}
                className="rounded-[8px]"
              >
                <CheckCircle2 className="size-4" />
                {t`Save changes`}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
